import React, { ReactNode } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedCurrency, FormattedMessage, useIntl } from '@divvy-web/i18n'
import Badge, { BADGE_COLOR_ACTION } from '@divvy-web/skylab.badge'
import BasicButton, { BASIC_BUTTON_TYPE_FILLED, BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'
import Tile, { TILE_SELECTED_COLOR_ACTION } from '@divvy-web/skylab.tile'

import Divider from '@sputnik/components/Divider'
import EligibleOfferTileLearnMoreLink from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/EligibleOfferTileLearnMoreLink/EligibleOfferTileLearnMoreLink'

import styles from './styles.module.scss'

interface EligibleOfferTileProps {
  onViewOfferDetailsClick: () => void
  approvedAmount?: number
  bulletPoints: ReactNode[]
  dataTestId?: string
  description: ReactNode
  isMostPopular?: boolean
  subtitle?: ReactNode
  title: ReactNode
  learnMoreLink: string
}

const EligibleOfferTile = ({
  onViewOfferDetailsClick,
  approvedAmount,
  bulletPoints,
  dataTestId,
  description,
  isMostPopular,
  subtitle,
  title,
  learnMoreLink,
}: EligibleOfferTileProps) => {
  const [getClassName, makeTestId] = useNameFormatter('EligibleOfferTile', { dataTestId })
  const { formatMessage } = useIntl()

  const hasApprovedAmmount = approvedAmount !== undefined && approvedAmount > 0

  return (
    <Tile
      hidePadding
      className={styles[getClassName('')]}
      dataTestId={dataTestId}
      isSelected={isMostPopular}
      /* COM-10325: hook up logic for isSelected
      - isSelected if agreement terms are agreed to for SBD
      - isSelected if they click on it for non-SBD
      */
      selectedColor={TILE_SELECTED_COLOR_ACTION}
    >
      <div
        className={styles[getClassName('inner')]}
        data-testid={makeTestId('')}
      >
        <div className={styles[getClassName('content-wrapper')]}>
          <div className={styles[getClassName('title-wrapper')]}>
            {isMostPopular && (
              <Badge color={BADGE_COLOR_ACTION}>
                <FormattedMessage
                  defaultMessage='Most popular'
                  id='sputnik.EligibleOfferTile__dC3u7Z'
                />
              </Badge>
            )}
            {hasApprovedAmmount && (
              <div
                className={styles[getClassName('amount')]}
                data-testid={makeTestId('amount')}
              >
                <FormattedCurrency
                  maximumFractionDigits={0}
                  minimumFractionDigits={2}
                  value={approvedAmount}
                />
              </div>
            )}
            <div className={styles[getClassName(hasApprovedAmmount ? 'subtitle' : 'title')]}>{title}</div>
            {subtitle && <div className={styles[getClassName('subtitle')]}>{subtitle}</div>}
          </div>
          <div className={styles[getClassName('description')]}>{description}</div>
          <Divider className={styles[getClassName('divider')]} />
          <div>
            <ul className={styles[getClassName('bullet-list')]}>
              {bulletPoints.map((point, index) => (
                <li
                  key={index}
                  className={styles[getClassName('bullet-item')]}
                >
                  <Icon
                    className={styles[getClassName('bullet-icon')]}
                    name='check'
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className={styles[getClassName('button-wrapper')]}>
          <BasicButton
            aria-label={formatMessage(
              {
                defaultMessage: 'Review offer summary {title}',
                id: 'sputnik.EligibleOfferTile__svTpEp',
              },
              { title: title as string },
            )}
            className={styles[getClassName('review-offer')]}
            data-testid={makeTestId('details')}
            type={isMostPopular ? BASIC_BUTTON_TYPE_FILLED : BASIC_BUTTON_TYPE_OUTLINED}
            onClick={onViewOfferDetailsClick}
          >
            <FormattedMessage
              defaultMessage='Review offer summary'
              id='sputnik.EligibleOfferTile__iJsPEe'
            />
          </BasicButton>
          <EligibleOfferTileLearnMoreLink url={learnMoreLink} />
        </div>
      </div>
    </Tile>
  )
}

export default EligibleOfferTile
